import React, { useContext } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { Box, CircularProgress, CssBaseline, ThemeProvider } from "@mui/material";
import { AuthContext } from "./AuthContext";
import { consultantTheme } from "./components/ConsultantDashboard/ConsultantTheme";

import Login from "./components/Login";
import ConsultantDashboard from "./components/ConsultantDashboard";
import AdminDashboard from "./components/AdminDashboard";
import MarketingDashboard from "./components/MarketingDashboard";
import NewInquiryFlow from "./components/NewInquiryFlow";
import InquiryLandingPage from "./components/InquiryLandingPage";
import ServiceProviderInquiryFlow from "./components/ServiceProviderInquiry";
import SuperAdminDashboard from "./components/SuperAdminDashboard";
import PublicReplyPage from "./components/PublicReplyPage";
import MassEmailingDashboard from "./pages/MassEmailingDashboard";
import WhatsAppInboxPage from "./pages/WhatsAppInboxPage";
import EmailInboxPage from "./pages/EmailInboxPage";
import UnifiedInboxMessagingPage from "./pages/UnifiedInboxMessagingPage";
import PhoneRotationsPage from "./pages/PhoneRotationsPage";
import EnterpriseSetupPage from "./pages/EnterpriseSetupPage";
import MetaEmbeddedSignupCallbackPage from "./pages/MetaEmbeddedSignupCallbackPage";
import WhatsAppLinesPage from "./pages/WhatsAppLinesPage";
import OmnichannelRealtimeHub from "./components/omnichannel/OmnichannelRealtimeHub";

function hasPermission(user, permission) {
  return Array.isArray(user?.permissions) && user.permissions.includes(permission);
}

function resolveDashboardPath(user) {
  const role = String(user?.role || "").toLowerCase();

  if (role === "superadmin") {
    return "/superadmin";
  }

  if (user?.is_admin || role === "admin") {
    return "/admin";
  }

  if (hasPermission(user, "workspace.marketing_access") || role === "marketing") {
    return "/marketing";
  }

  if (hasPermission(user, "workspace.consultant_access") || role === "consultant") {
    return "/consultant";
  }

  if (hasPermission(user, "omnichannel.access")) {
    return "/omnichannel/inbox";
  }

  return "/login";
}

function AppRoutes() {
  const { user, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  const role = String(user?.role || "").toLowerCase();
  const dashboardPath = user ? resolveDashboardPath(user) : "/login";
  const canSeeAdmin = Boolean(user?.is_admin) || role === "admin" || role === "superadmin";
  const canSeeSuperAdmin = role === "superadmin";
  const canSeeConsultant = canSeeAdmin || hasPermission(user, "workspace.consultant_access") || role === "consultant";
  const canSeeMarketing = canSeeAdmin || hasPermission(user, "workspace.marketing_access") || role === "marketing";
  const canSeeOmnichannel = hasPermission(user, "omnichannel.access");
  const canManageEnterprise = hasPermission(user, "enterprise.setup.manage") || canSeeAdmin;
  const canSeePhoneRotations = canSeeAdmin || canSeeOmnichannel;
  const canSeeWhatsAppLines = canSeeAdmin || canSeeOmnichannel;

  return (
    <>
      {user ? <OmnichannelRealtimeHub /> : null}
      <Routes>
        {!user ? <Route path="/" element={<InquiryLandingPage />} /> : null}
        <Route path="/new-inquiry" element={<NewInquiryFlow />} />
        <Route path="/r/reply" element={<PublicReplyPage />} />
        <Route path="/service-provider-inquiry" element={<ServiceProviderInquiryFlow />} />
        <Route path="/meta/embedded-signup/callback" element={<MetaEmbeddedSignupCallbackPage />} />

        {!user ? (
          <>
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Navigate to={dashboardPath} replace />} />
            <Route path="/login" element={<Navigate to={dashboardPath} replace />} />

            <Route
              path="/superadmin"
              element={canSeeSuperAdmin ? <SuperAdminDashboard /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/consultant"
              element={canSeeConsultant ? <ConsultantDashboard /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/admin"
              element={canSeeAdmin ? <AdminDashboard /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/marketing"
              element={canSeeMarketing ? <MarketingDashboard /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/marketing/mass-emailing"
              element={canSeeMarketing ? <MassEmailingDashboard /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/marketing/whatsapp-inbox"
              element={canSeeMarketing ? <WhatsAppInboxPage /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/marketing/email-inbox"
              element={canSeeMarketing ? <EmailInboxPage /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/omnichannel/inbox"
              element={canSeeOmnichannel ? <UnifiedInboxMessagingPage /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/omnichannel/phone-rotations"
              element={canSeePhoneRotations ? <PhoneRotationsPage /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/omnichannel/whatsapp-lines"
              element={canSeeWhatsAppLines ? <WhatsAppLinesPage /> : <Navigate to={dashboardPath} replace />}
            />

            <Route
              path="/enterprise/setup"
              element={canManageEnterprise ? <EnterpriseSetupPage /> : <Navigate to={dashboardPath} replace />}
            />

            <Route path="/marketing/email" element={<Navigate to="/marketing/mass-emailing" replace />} />
            <Route path="*" element={<Navigate to={dashboardPath} replace />} />
          </>
        )}
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={consultantTheme}>
      <CssBaseline />
      <Router>
        <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}
